import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { mdiArrowRight, mdiPlus, mdiUpdate } from "@mdi/js";
import Icon from "@mdi/react";

import Button from "components/Button";
import CustomMap from "components/CustomMap";
import Header from "components/Header";
import { routes } from "constants/routes";
import { convertLoadData } from "helpers/convertFormData";
import { formatNumber } from "helpers/formatNumber";
import { showErrorMessage } from "helpers/toastMessage";
import MainLayout from "layouts/Main";
import { ILoadQuestion } from "models/Load";
import { selectAccount } from "store/auth/selector";
import LoadPostedItem from "../../modules/LoadPostedItem/LoadPostedItem";
import LoadQuestion from "../../modules/LoadQuestion";
import { QUERY_LOAD_DETAIL, QUERY_LOAD_QUESTION } from "../query";
import LoadDetailSkeleton from "../LoadDetailSkeleton";
import styles from "./style.module.scss";
import useDeviceType from "hooks/useDeviceType";

interface IProps {
  label: string;
  children: React.ReactNode;
}

function LabeledItem({ label, children }: Readonly<IProps>) {
  return (
    <div className={styles.labeledItem}>
      <span className={styles.label}>{label}</span>
      <div className={styles.content}>{children}</div>
    </div>
  );
}

export default function MyLoadDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loadId } = useParams();
  const { isDesktop } = useDeviceType();
  const { userPermission } = useSelector(selectAccount);

  const { data, error, refetch } = useQuery(QUERY_LOAD_DETAIL, {
    variables: { id: loadId },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network"
  });

  const loadData = useMemo(() => {
    if (!data) return null;
    return {
      ...convertLoadData(data.load),
      coords: [
        ...(data.load.originCoords
          ? [{ lat: data.load.originCoords.latitude, lng: data.load.originCoords.longitude }]
          : []),
        ...(data.load.destinationCoords
          ? [
              {
                lat: data.load.destinationCoords.latitude,
                lng: data.load.destinationCoords.longitude
              }
            ]
          : [])
      ],
      centerCoords: data.load.originCoords
        ? { lat: data.load.originCoords.latitude, lng: data.load.originCoords.longitude }
        : { lat: 0, lng: 0 }
    };
  }, [data]);

  const viewLaneRateTrends = () => {
    navigate(location.pathname + "/lane-trends", {
      state: {
        origin_city: loadData.originCity,
        origin_state: loadData.originState,
        destination_city: loadData.destinationCity,
        destination_state: loadData.destinationState
      }
    });
  };

  const {
    data: questionData,
    error: questionError,
    refetch: questionRefetch
  } = useQuery(QUERY_LOAD_QUESTION, {
    variables: { loadId: loadId },
    fetchPolicy: "cache-and-network"
  });

  const loadQuestion: ILoadQuestion[] = useMemo(() => {
    if (questionData?.loadQuestions) {
      return questionData.loadQuestions.data ?? [];
    }
    return [];
  }, [questionData]);

  useEffect(() => {
    if (error) {
      showErrorMessage(error.message);
    }
    if (questionError) {
      showErrorMessage(questionError.message);
    }
  }, [error, questionError]);

  if (!loadData) return <LoadDetailSkeleton title="My Load Details" />;

  const goToUserProfile = (id: string) => {
    navigate(routes.USER + `/${id}`);
  };

  return (
    <MainLayout
      desktopTitle="My Loads"
      className="!pr-14 !pl-16 mobile:!px-5"
      customHeader={<Header label="My Load Details" />}>
      <div className={styles.loadDetailBody}>
        <span className={styles.loadNumber}>Load #: {loadData?.userLoadNumber}</span>

        <button type="button" className={styles.refresh} onClick={() => refetch()}>
          <Icon path={mdiUpdate} className="w-5" />
          <span>Refresh Age</span>
        </button>

        <LoadPostedItem
          data={loadData}
          editable={false}
          showFooter={false}
          questionRefresh={questionRefetch}
          showStatus
        />

        <LabeledItem label="Notes:">
          <span>{loadData.details ?? "No details yet"}</span>
        </LabeledItem>
        <LabeledItem label="Target Rate:">
          <span>{formatNumber(loadData.targetRate / 100, 2)}</span>
        </LabeledItem>
        <LabeledItem label="Answer Question:">
          <div className={styles.loadDetailAsk}>
            {loadQuestion.length ? (
              <LoadQuestion
                goToUserProfile={goToUserProfile}
                loadQuestion={loadQuestion}
                refresh={questionRefetch}
                isMyLoad
              />
            ) : (
              <span className="text-sm text-placeholder">No questions posted yet.</span>
            )}
          </div>
        </LabeledItem>
        <LabeledItem label="Market Average:">
          <span>{loadData.linehaulRate ?? "Based on truckload bid trend for this lane."}</span>
        </LabeledItem>

        {isDesktop ? (
          <button
            type="button"
            onClick={viewLaneRateTrends}
            className="flex gap-2 items-center my-4 w-fit disabled:!cursor-not-allowed"
            disabled={!userPermission.rate_trend_on_load_details}>
            <Icon path={mdiPlus} className="size-7" />
            <span className="mt-0.5">View Lane Rate Trend</span>
          </button>
        ) : (
          <Button
            variant="outline"
            className="justify-between py-4 my-4 disabled:!cursor-not-allowed"
            onClick={viewLaneRateTrends}
            disabled={!userPermission.rate_trend_on_load_details}>
            <span className="text-link">View Lane Rate Trend</span>
            <Icon path={mdiArrowRight} />
          </Button>
        )}

        <div className={styles.loadDetailMap}>
          <h4 className="font-medium">Map:</h4>

          <CustomMap points={loadData.coords} center={loadData.centerCoords} isDirections />
        </div>

        <div className={styles.loadDetailDirection}>
          <h4 className="font-medium">Directions:</h4>

          <p className="text-xs font-light">
            Please note that directions may be different from truck route specific GPS directions.
          </p>

          <a
            href={`https://www.google.com/maps/dir/?api=1&origin=${loadData.originCoords?.latitude},${loadData.originCoords?.longitude}&destination=${loadData.destinationCoords?.latitude},${loadData.destinationCoords?.longitude}&travelmode=driving`}
            target="_blank"
            rel="noreferrer"
            className={styles.loadDetailDirectionLink}>
            <span className="flex text-lg underline">Open Directions</span>
            <Icon path={mdiArrowRight} size="24px" />
          </a>
        </div>

        <div className={styles.loadDetailReturn}>
          <h3 className="font-medium">Return Loads:</h3>
          <p className="text-xs font-light">Nothing here yet.</p>
        </div>
      </div>
    </MainLayout>
  );
}
